import React from "react";
import { useState, useEffect, useRef } from "react";
import "react-html5-camera-photo/build/css/index.css";
import {
  Box,
  Button,
  Image,
  Text,
  TextInput,
  Layer,
} from "grommet";
import { Camera, Gallery, FormAttachment, FormTrash, FormClose } from "grommet-icons";
import { updateStateHandler } from "../Utils/Utils";
import WarningPopupWindow from "../Base components/WarningPopupWindow";
import DangerPopupWindow from "../Base components/DangerPopupWindow";
import pica from 'pica';

const BaseCamera = (props, { onSubmit, ...rest }) => {

    // console.log('props in BaseCamera >> ', props);
    const [image, setImage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [deviceNotAvailMsg, setDeviceNotAvailMsg] = useState('');
    const [dangerPopupOpen, setDangerPopupOpen] = useState(false); //show/hide Danger Popup Window
    const [dangerMessage, setDangerMessage] = useState('');
    const [dangerFuncOnConfirm, setDangerFuncOnConfirm] = useState('');

    const [stream, setStream] = useState(null);
    const [isMounted, setIsMounted] = useState(true); // <-- add this state variable

    const fileInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const openFileDialog = () => {
        fileInputRef.current.click();
    };

    const closeWarning = () => {
        setWarningMessage('');
        setShowWarning(false);        
    }

    // opens a popup window when the delete" button is clicked
    const openDangerPopupWindow = (msg, func) => {
        setDangerMessage(msg);
        setDangerFuncOnConfirm(() => func); // Store the callback function without executing it
        setDangerPopupOpen(true);
    }

    // hides the "Danger" popup window 
    const onDangerPopupClose = () => {
        setDangerPopupOpen(false);
        setDangerMessage('');    
    };

    // fires when the "Yes" button is clicked in the "Danger" popup
    const onDangerConfirm = () => {
        // console.log(' << in function onDangerConfirm >> '); 
        if (typeof dangerFuncOnConfirm === 'function') {
            dangerFuncOnConfirm();
        } 
        onDangerPopupClose();
        setDangerFuncOnConfirm('');
    }; 

    const deleteImage = () => {
        updateStateHandler(props.stateHandler, props.fieldName, '');
        setImage('');
        setShowImage(false)
    }

    const onFilesAdded = (event) => {
        // console.log("onFilesEntered");
        let file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = e.target.result;

            // Get the file type
            const fileType = file.type;

            // Check the file type
            if (fileType === 'image/png' || fileType === 'image/jpeg') {
                // Compress the image using the compressImage function
                openPicture(img, fileType);
            } else {
                // Handle the case when the uploaded file is not an image
                console.warn('Invalid file type. Only PNG and JPEG files are supported.');
                setWarningMessage("Invalid file type. Only PNG and JPEG files are supported."); 
                setShowWarning(true);
                return;
            }            
        };
        reader.onabort = () => console.log("file reading was aborted");
        reader.onerror = () => console.log("file reading has failed");
        if (file) {
            reader.readAsDataURL(file);
        }
    };

    const openPicture = (imageData, fileType) => {
        const imageElement = new window.Image();
        imageElement.src = imageData;

        // When the image has loaded, get the width and heigh
        imageElement.onload = async() => {
            const maxWidth = 500; // Maximum width for the compressed image
            const maxHeight = maxWidth / (16 / 9); // Maximum height for the compressed image
  
            let newWidth = imageElement.naturalWidth;
            let newHeight = imageElement.naturalHeight;

            // console.log('Image width:', imageElement.naturalWidth);
            // console.log('Image height:', imageElement.naturalHeight);

            if (newWidth > maxWidth) {
              newWidth = maxWidth;
              newHeight = (newWidth / imageElement.naturalWidth) * imageElement.naturalHeight;
            }
  
            if (newHeight > maxHeight) {
              newHeight = maxHeight;
              newWidth = (newHeight / imageElement.naturalHeight) * imageElement.naturalWidth;
            }

            // Compress the image using pica library
            const compressedDataURL = await compressImage(imageElement.src, newWidth, newHeight, fileType);
        
            // Set the compressed data URL as the src property of an img tag
            setImage(compressedDataURL);  
        
            // Send the compressed data URL to the server
            updateStateHandler(props.stateHandler, props.fieldName, compressedDataURL);                               
        }       
    }    

    // takes image from camera
    const takePicture = async () => {
        if (videoRef.current && canvasRef.current) {
            let width = 500;
            let height = width / (16 / 9);
        
            let canvas = canvasRef.current;
            let video = videoRef.current;
        
            canvas.width = width;
            canvas.height = height;
        
            let ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
            // Convert canvas image to base64 data URL
            let dataURL = canvas.toDataURL('image/jpeg');
            
            // Compress the image using pica library
            const compressedDataURL = await compressImage(dataURL, width, height, 'jpeg');
        
            // Set the compressed data URL as the src property of an img tag
            setImage(compressedDataURL);
        
            // Send the compressed data URL to the server
            updateStateHandler(props.stateHandler, props.fieldName, compressedDataURL);

        }
    };
      
    const compressImage = (dataURL, width, height, fileType) => {
        return new Promise((resolve) => {
            const img = new window.Image();
            img.src = dataURL;
            img.onload = async () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
        
                // Check if the provided dimensions are valid
                if (width <= 0 || height <= 0) {
                    resolve(dataURL);
                    return;
                }
        
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
        
                const picaInstance = pica();
                const targetSize = 20000; // Specify the desired target size in bytes
                const resultCanvas = await picaInstance.resize(canvas, canvas, { quality: 0.8 }); // Adjust quality as needed


                // Convert the result canvas to the appropriate data URL based on the file type
                let resultDataURL;

                if (fileType === 'image/jpeg') {
                    resultDataURL = resultCanvas.toDataURL('image/jpeg', 0.8); // Adjust quality as needed
                } else if (fileType === 'image/png') {
                    resultDataURL = resultCanvas.toDataURL('image/png'); // Adjust quality as needed
                } else {
                    // Unsupported file type, resolve with the original data URL
                    resolve(dataURL);
                    return;
                }

                if (resultDataURL.length < targetSize) {
                    if (fileType === 'image/png'){
                        resolve(resultDataURL);
                    } else if (fileType === 'image/jpeg') {
                        resolve(convertToJFIF(resultDataURL));
                    }  
                } else {
                    const smallerWidth = Math.floor(width * 0.9); // Adjust dimensions as needed
                    const smallerHeight = Math.floor(height * 0.9); // Adjust dimensions as needed
                    resolve(await compressImage(resultDataURL, smallerWidth, smallerHeight, fileType));
                }
            };
        });
    };
      
    const convertToJFIF = (dataURL) => {
        // Convert the dataURL from JPEG format to JFIF format
        const jfifDataURL = dataURL.replace('data:image/jpeg', 'data:image/jfif');
        return jfifDataURL;
    };
      

    useEffect(() => {
        if (props.initValue !== null && props.initValue !== undefined) {
            
            const openPicture = async (imageData) => {
                const imageBuffer = Buffer.from(props.initValue, 'binary');
                const imageElement = new window.Image();
                imageElement.src = imageBuffer;

                // Compress the image using pica library
                const compressedDataURL = await compressImage(imageElement.src, imageElement.naturalWidth, imageElement.naturalHeight, 'jpeg');
            
                // Set the compressed data URL as the src property of an img tag
                setImage(compressedDataURL);  
            
                // Send the compressed data URL to the server
                updateStateHandler(props.stateHandler, props.fieldName, compressedDataURL);                     

                // console.log('imageBuffer:', imageBuffer);
                // console.log('Image source:', imageElement.src);
                // console.log('Image width:', imageElement.naturalWidth);
                // console.log('Image height:', imageElement.naturalHeight);
            }

            openPicture(props.initValue);
        } else {
            setImage('');            
        }
    
    }, []);
    
    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);

    useEffect(() => {
        setIsMounted(true); // <-- set isMounted to true when the component mounts

        // get access to user web camera
        async function getMedia() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true
                });
                if (isMounted) { // <-- check if the component is still mounted
                    setStream(stream);
                }
            } catch (err) {
                console.warn('In getMedia: '+ err.name + ': '+ err.message); /* handle the error */
                setDeviceNotAvailMsg("Camera not available");
                //setWarningMessage("Camera not available");                 
                
                if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
                    //required track is missing 

                } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
                    //webcam or mic are already in use 

                } else if (err.name === "OverconstrainedError" || err.name === "ConstraintNotSatisfiedError") {
                    //constraints can not be satisfied by avb. devices 

                } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
                    //permission denied in browser 

                } else if (err.name === "TypeError" || err.name === "TypeError") {
                    //empty constraints object 

                } else {
                    //other errors 

                }
            }
        }

        getMedia();
        
        return () => {
          setIsMounted(false); // <-- set isMounted to false when the component unmounts
        };
    }, [isMounted]); // <-- add isMounted as a dependency of the effect


  return (
    <Box direction="row" width="270px" justify="around" align="start" >
        { props.title !== undefined && 
        <Box  width="120px" >
            <Text  alignSelf="start" color="brand" size="18px" style={{ padding: "5px 0 ", margin: "5px 0 0 0", whiteSpace: 'nowrap', display: 'inline-block'}} >
                {props.title}
            </Text>
        </Box>
        }
        <video ref={videoRef} style={{display: "none"}} autoPlay></video>
        <canvas ref={canvasRef} style={{display: "none"}} ></canvas>
        <Box direction="row" alignSelf="start">
            <Button
                width="48px"
                height="48px"
                icon={<Camera />}
                disabled={props.disable}
                onClick={() => {
                    if (deviceNotAvailMsg === '') {
                        if (image.length > 0) {
                            openDangerPopupWindow("The image will be changed to a new one and then will be saved! Are you sure you want to continue?",
                                takePicture
                            );
                        } else {
                            takePicture();
                        }
                    } else {
                        setWarningMessage(deviceNotAvailMsg);
                        setShowWarning(true);
                    }
                }}                
            />
            <Button 
                maxWidth="48px" 
                maxHeight="48px" 
                icon={<Gallery />} 
                onClick={() => {
                    {image.length > 0 ?
                        openDangerPopupWindow("The image will be changed to a new one and then will be saved! Are you sure you want to continue?", openFileDialog)
                        :
                        openFileDialog()                        
                    }}}                
                disabled={props.disable}
            />
          
            {image.length > 0 ? (
            <Button
                width="48px"
                height="48px"
                style={{padding: '6px'}}
                icon={<FormAttachment size="34px"  />}
                onClick={() => {
                    setShowImage(true);
                }}
            /> 
            )
            :
            <Box 
                width="48px"
                height="48px"/>
            } 
          

            <TextInput
                hidden
                ref={fileInputRef}
                type="file"
                multiple
                onChange={onFilesAdded}
            />                  
        </Box>

        {showWarning && (
            <WarningPopupWindow msg={warningMessage} close={closeWarning}/>
        )}

        {dangerPopupOpen && (
        <DangerPopupWindow
            msg={dangerMessage}
            onClose={onDangerPopupClose}
            onConfirm={onDangerConfirm}
        />
        )}

        {showImage && (
        <Layer
             onEsc={() => setShowImage(false)}
             onClickOutside={() => {}}
             responsive={true}
             background="light-3"
         >
            <Box pad="xsmall" margin="small" border={{ color: "brand"}} >
                <Box align="end"  background="#e9defa" pad="xxsmall" >
                    <Button
                        style={{borderRadius: "0"}}
                        icon={<FormClose/>}
                        color='brand'
                        plain={true}
                        width="30px"
                        height="30px"
                        onClick={() => setShowImage(false)}
                        primary />
                </Box>
                <Box pad="xxsmall">
                    <Image
                        style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                        }}
                    fit="cover"
                    src={image}
                    /> 
                </Box>  
                <Box pad="xxsmall" direction="row" margin="xxsmall" justify="around" >     
                {!props.disable &&
                    <Button 
                        color="#801204"
                        icon={<FormTrash />}
                        primary                        
                        // style={{display: "none"}}
                        label="delete" 
                        onClick={() => openDangerPopupWindow("The image will be deleted after saving! Are you sure you want to continue?", deleteImage)}
                    />
                }
                </Box>
            </Box>            
        </Layer>
       )}      
    </Box>
  );
};
export default BaseCamera;
